import { Injectable, Signal, signal, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export interface SupportedLanguage {
  code: string;
  label: string;
  flag: string;
}

const STORAGE_KEY = 'language';
const DEFAULT_LANG = 'en';

const BROWSER_LANG_MAP: Record<string, string> = {
  en: 'en',
  nb: 'no', nn: 'no', no: 'no',
  da: 'dk', dk: 'dk',
  sv: 'se', se: 'se',
  de: 'de',
};

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private translate = inject(TranslateService);

  readonly SUPPORTED: SupportedLanguage[] = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'no', label: 'Norsk', flag: '🇳🇴' },
    { code: 'dk', label: 'Dansk', flag: '🇩🇰' },
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
    { code: 'se', label: 'Svenska', flag: '🇸🇪' },
  ];

  private readonly _current = signal<string>(DEFAULT_LANG);
  readonly current: Signal<string> = this._current.asReadonly();

  init(): void {
    this.translate.setDefaultLang(DEFAULT_LANG);
    this.apply(this.resolveInitialLang());
  }

  setLanguage(code: string): void {
    if (!this.isSupported(code)) return;
    this.apply(code);
  }

  private apply(code: string): void {
    this.translate.use(code);
    localStorage.setItem(STORAGE_KEY, code);
    this._current.set(code);
  }

  private resolveInitialLang(): string {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && this.isSupported(stored)) return stored;
    const browser = (this.translate.getBrowserLang() || '').toLowerCase();
    return BROWSER_LANG_MAP[browser] ?? DEFAULT_LANG;
  }

  private isSupported(code: string): boolean {
    return this.SUPPORTED.some((l) => l.code === code);
  }
}
